/**
 * BlocSection (zone [5]) — porte les 5 variantes de corps : `dl`, `chips`,
 * `banner`, `contacts` (liste multi-contacts façon puce carte + enveloppe par
 * contact), `gauge` (jauge santé + `dl`). Titre UPPERCASE muted (harmonisé §0).
 */

import { Mail, Star, UserX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { LocalTimeHint } from '@/components/shared/LocalTimeHint'

import { SummaryIcon } from '../primitives'
import { formatSummaryValue, toneChipClass, toneValueClass } from '../primitives.helpers'
import type { SummaryItem, SummarySection } from '../types'
import { SummaryGauge } from './SummaryGauge'

interface SectionProps {
    section: SummarySection
    onSendEmail: (target: { email: string; name: string }) => void
    onNavigate: (href: string) => void
}

/**
 * Titre de section — texte SEUL, jamais d'icône.
 *
 * ⚠️ Il en portait une. Retirée le 2026-08-11 sur retour PO : depuis que chaque
 * LIGNE a la sienne, une icône de plus en tête de section faisait perdre la
 * hiérarchie — l'œil ne distinguait plus le titre de son contenu. C'est
 * exactement ce que fait `MapCardSection` sur la carte de survol, la
 * présentation de référence : un filet, un libellé gris en majuscules, rien
 * d'autre.
 *
 * Le contrat continue de porter `section.icon` : le serveur nomme le RÔLE de la
 * section, information vraie et peu coûteuse, que cet arbitrage de présentation
 * n'annule pas — et qui redevient utile si le PO tranche l'inverse.
 */
function SectionTitle({ title }: { title: string }) {
    const { t } = useTranslation()
    return (
        <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t(title, title)}
        </div>
    )
}

/**
 * Libellé d'une ligne — icône EN TÊTE, comme `MapCardStat` sur la carte de
 * survol (présentation de référence, PO 2026-08-11). L'icône était auparavant
 * accolée à la valeur : elle qualifiait donc le contenu au lieu d'annoncer la
 * ligne, et se retrouvait au milieu du panneau.
 */
function DlLabel({ item }: { item: SummaryItem }) {
    const { t } = useTranslation()
    return (
        <dt className="flex items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
            <SummaryIcon name={item.icon} className="h-3.5 w-3.5 shrink-0" />
            <span>{t(item.label, item.label)}</span>
        </dt>
    )
}

function DlValue({ item, onNavigate }: { item: SummaryItem; onNavigate: (href: string) => void }) {
    const { t, i18n } = useTranslation()
    // `timezone` : rendu par un COMPOSANT (heure vivante + écart avec le lecteur),
    // pas par une fonction de formatage — il n'y a pas de chaîne à produire, il y
    // a une horloge à faire tourner. Son horloge lui appartient : c'est pourquoi
    // `summary_item_icons` ne donne PAS d'icône à cette ligne (on en aurait deux).
    if (item.format === 'timezone') {
        return (
            <dd className="text-xs">
                <LocalTimeHint timeZone={item.value} />
            </dd>
        )
    }
    // `enum` : la valeur est une clé i18n métier (nature/modalité…) → traduite.
    const text =
        item.format === 'enum'
            ? t(item.value, { defaultValue: item.value, ...(item.params ?? {}) })
            : formatSummaryValue(item.value, item.format, item.currency, i18n.language, item.tz)
    const content = (
        <>
            {item.href ? (
                <button
                    type="button"
                    onClick={() => onNavigate(item.href!)}
                    className="font-medium text-primary underline decoration-1 underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    {text}
                </button>
            ) : (
                <span className={cn(item.mono && 'font-mono text-[11px]', toneValueClass(item.tone))}>
                    {text}
                </span>
            )}
            {item.badge && (
                <span
                    className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-1.5 py-px text-[10px] font-semibold',
                        toneChipClass(item.badge.tone),
                    )}
                >
                    <SummaryIcon name={item.badge.icon} className="h-3 w-3" />
                    {item.badge.label ? t(item.badge.label, item.badge.label) : null}
                </span>
            )}
        </>
    )
    // Valeur JUSTE APRÈS le libellé, en gras — et non poussée au bord droit du
    // panneau. C'est le reproche précis du PO : sur une ligne large, l'œil doit
    // traverser le vide pour relier « Ville » à « Grenoble ». La carte les colle,
    // et c'est ce qui la rend lisible d'un coup d'œil.
    return (
        <dd className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-foreground">
            {content}
        </dd>
    )
}

/**
 * Une ligne complète : « icône · libellé gris · valeur en gras », sur un seul
 * flux — la maille de `MapCardStat`.
 *
 * ⚠️ Ce n'est PLUS une grille à deux colonnes. Une grille aligne les valeurs sur
 * le libellé le plus long de la section : « Ville : Grenoble » se retrouvait
 * décalé par « Passeport de prévention » trois lignes plus bas. C'était une
 * seconde version du défaut signalé (valeur éloignée de son libellé), plus
 * discrète que l'alignement à droite mais de même nature.
 */
function DlRow({ item, onNavigate }: { item: SummaryItem; onNavigate: (href: string) => void }) {
    return (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <DlLabel item={item} />
            <DlValue item={item} onNavigate={onNavigate} />
        </div>
    )
}

export function SummarySectionBlock({ section, onSendEmail, onNavigate }: SectionProps) {
    const { t } = useTranslation()

    // ── banner (rattachement) : pas de titre ──────────────────────────────────
    if (section.layout === 'banner' && section.banner) {
        const b = section.banner
        const inner = (
            <>
                <SummaryIcon name={b.icon} className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">
                    <span className="text-muted-foreground">{t('summaryDrawer.attachedTo', 'Rattaché à')} </span>
                    {b.label}
                </span>
                {b.star && (
                    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium', toneChipClass('primary'))}>
                        <Star className="h-3 w-3" aria-hidden="true" />
                        {t('summaryDrawer.primarySite', 'Site principal')}
                    </span>
                )}
            </>
        )
        return b.href ? (
            <button
                type="button"
                onClick={() => onNavigate(b.href!)}
                className="flex w-full items-center gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                {inner}
            </button>
        ) : (
            <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2.5 text-xs">{inner}</div>
        )
    }

    // ── gauge (santé) : jauge + dl ────────────────────────────────────────────
    if (section.layout === 'gauge' && section.gauge) {
        return (
            <section>
                <SectionTitle title={section.title} />
                <div className="mb-3.5">
                    <SummaryGauge gauge={section.gauge} />
                </div>
                {section.items.length > 0 && (
                    <dl className="space-y-1.5">
                        {section.items.map((item, i) => (
                            <DlRow key={`${item.label}-${i}`} item={item} onNavigate={onNavigate} />
                        ))}
                    </dl>
                )}
            </section>
        )
    }

    // ── contacts (liste multi-contacts + enveloppe par contact) ───────────────
    if (section.layout === 'contacts') {
        return (
            <section>
                <SectionTitle title={section.title} />
                {section.contacts.length === 0 && section.empty_text ? (
                    <div className="flex items-center gap-2 text-xs italic text-muted-foreground">
                        <UserX className="h-3.5 w-3.5" aria-hidden="true" />
                        {section.empty_text}
                    </div>
                ) : (
                    <div className="divide-y divide-border/60">
                        {section.contacts.map((c, i) => (
                            <div key={`${c.name}-${i}`} className="flex items-center justify-between gap-3.5 py-2">
                                <div className="min-w-0 text-sm leading-snug">
                                    <span className="font-semibold text-foreground">{c.name}</span>
                                    {c.function && (
                                        <span className="text-muted-foreground"> · {c.function}</span>
                                    )}
                                </div>
                                {c.email && (
                                    <button
                                        type="button"
                                        onClick={() => onSendEmail({ email: c.email!, name: c.name })}
                                        aria-label={t('summaryDrawer.actions.email', 'Envoyer un email')}
                                        title={t('summaryDrawer.actions.email', 'Envoyer un email')}
                                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-foreground hover:border-primary/40 hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                    >
                                        <Mail className="h-[17px] w-[17px]" aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        )
    }

    // ── chips (accès / régimes / services) ────────────────────────────────────
    if (section.layout === 'chips') {
        return (
            <section>
                <SectionTitle title={section.title} />
                {section.chips.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {section.chips.map((c, i) => (
                            <span
                                key={`${c.label}-${i}`}
                                className={cn(
                                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                                    toneChipClass(c.tone ?? 'primary'),
                                )}
                            >
                                <SummaryIcon name={c.icon} className="h-3 w-3" />
                                {t(c.label, c.label)}
                            </span>
                        ))}
                    </div>
                )}
                {section.subline && (
                    <div className="mt-2.5 text-xs text-muted-foreground">{section.subline}</div>
                )}
            </section>
        )
    }

    // ── dl (défaut) ───────────────────────────────────────────────────────────
    return (
        <section>
            <SectionTitle title={section.title} />
            {section.items.length === 0 && section.empty_text ? (
                <div className="flex items-center gap-2 text-xs italic text-muted-foreground">
                    <UserX className="h-3.5 w-3.5" aria-hidden="true" />
                    {section.empty_text}
                </div>
            ) : (
                <dl className="space-y-1.5">
                    {section.items.map((item, i) => (
                        <DlRow key={`${item.label}-${i}`} item={item} onNavigate={onNavigate} />
                    ))}
                </dl>
            )}
        </section>
    )
}
